/**
 * One of exactly two pieces of global client state (docs/16 section 7.4).
 * Holds whether there is a session; never holds business data.
 */
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { getPublicConfig, loadPublicConfig, refreshSession, setSessionLostHandler } from '../../api/client'
import { clearSession, getAccessToken, getRefreshToken } from '../../api/session'
import { consumeAuthCallback } from '../../features/auth/supabase'

type AuthStatus = 'loading' | 'signed-in' | 'signed-out' | 'unconfigured'

interface AuthValue {
  status: AuthStatus
  /**
   * Why the last sign-in attempt failed, when the provider refused it. It has to
   * live here because the refusal arrives on a page load, before the login
   * screen exists to receive it.
   */
  authError: string | null
  signOut: () => void
  markSignedIn: () => void
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [authError, setAuthError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function boot() {
      try {
        const config = await loadPublicConfig()
        if (cancelled) return
        if (!config.auth_enabled) {
          // The backend is running without authentication. That is a valid local
          // configuration but this app has no anonymous mode, so say so plainly
          // rather than rendering an empty board.
          setStatus('unconfigured')
          return
        }
        const callback = consumeAuthCallback()
        if (callback.error) {
          // A refused handshake leaves nothing usable behind; drop whatever is
          // held so the user starts clean rather than half signed in.
          clearSession()
          setAuthError(callback.error)
          setStatus('signed-out')
          return
        }
        if (callback.ok) {
          setStatus('signed-in')
          return
        }
        if (getAccessToken()) {
          setStatus('signed-in')
          return
        }
        if (getRefreshToken() && (await refreshSession())) {
          if (!cancelled) setStatus('signed-in')
          return
        }
        if (!cancelled) setStatus('signed-out')
      } catch (error) {
        // Cannot read the server config: not a signed-out user, a broken server.
        console.error('Could not load the server configuration.', error)
        if (!cancelled) setStatus('unconfigured')
      }
    }

    void boot()
    return () => {
      cancelled = true
    }
  }, [])

  const signOut = useCallback(() => {
    clearSession()
    setAuthError(null)
    setStatus('signed-out')
  }, [])

  useEffect(() => {
    setSessionLostHandler(() => setStatus('signed-out'))
  }, [])

  const value = useMemo<AuthValue>(
    () => ({
      status,
      authError,
      signOut,
      markSignedIn: () => {
        setAuthError(null)
        setStatus('signed-in')
      },
    }),
    [status, authError, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used inside AuthProvider')
  return value
}

export function authConfigured(): boolean {
  return Boolean(getPublicConfig()?.auth_enabled)
}
